import { useCallback, useEffect, useRef, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';

export function hasPlainTextDrag(dt: DataTransfer): boolean {
    return Array.from(dt.types as unknown as string[]).includes('text/plain');
}

type PlainTextDropOptions = {
    enabled: boolean;
    onTextDropped: (text: string) => Promise<void>;
    onFileOnlyDrop?: () => void;
};

/**
 * Shared drag-and-drop for plain text (e.g. email snippets) to drive client extraction flows.
 * Clears highlight on window dragend/drop too, since portaled MUI dialogs and drags
 * originating outside the page don't always emit a dragleave on the target.
 */
export function usePlainTextClientDrop(options: PlainTextDropOptions) {
    const theme = useTheme();
    const [textDragActive, setTextDragActive] = useState(false);
    const [busy, setBusy] = useState(false);
    const lockRef = useRef(false);
    // dragenter/dragleave also fire for descendants, so track nesting depth instead of a boolean.
    const dragDepthRef = useRef(0);
    const onTextRef = useRef(options.onTextDropped);
    onTextRef.current = options.onTextDropped;
    const onFileRef = useRef(options.onFileOnlyDrop);
    onFileRef.current = options.onFileOnlyDrop;

    useEffect(() => {
        if (!options.enabled) return;
        const clear = () => {
            dragDepthRef.current = 0;
            setTextDragActive(false);
        };
        window.addEventListener('dragend', clear);
        window.addEventListener('drop', clear);
        return () => {
            window.removeEventListener('dragend', clear);
            window.removeEventListener('drop', clear);
        };
    }, [options.enabled]);

    const handleDragOver = useCallback(
        (e: React.DragEvent) => {
            if (!options.enabled) return;
            if (hasPlainTextDrag(e.dataTransfer)) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
            }
        },
        [options.enabled],
    );

    const handleDragEnter = useCallback(
        (e: React.DragEvent) => {
            if (!options.enabled) return;
            if (hasPlainTextDrag(e.dataTransfer)) {
                e.preventDefault();
                dragDepthRef.current += 1;
                setTextDragActive(true);
            }
        },
        [options.enabled],
    );

    const handleDragLeave = useCallback(
        (e: React.DragEvent) => {
            if (!options.enabled) return;
            if (!hasPlainTextDrag(e.dataTransfer)) return;
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0) {
                setTextDragActive(false);
            }
        },
        [options.enabled],
    );

    const handleDrop = useCallback(
        async (e: React.DragEvent) => {
            if (!options.enabled) return;
            e.preventDefault();
            dragDepthRef.current = 0;
            setTextDragActive(false);
            const text = e.dataTransfer.getData('text/plain');
            if (!text?.trim()) {
                if (e.dataTransfer.files?.length) {
                    onFileRef.current?.();
                }
                return;
            }
            if (lockRef.current) return;
            lockRef.current = true;
            setBusy(true);
            try {
                await onTextRef.current(text);
            } finally {
                lockRef.current = false;
                setBusy(false);
            }
        },
        [options.enabled],
    );

    const dropHighlightSx = textDragActive
        ? {
              outline: `2px dashed ${theme.palette.primary.main}`,
              outlineOffset: 4,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
          }
        : {};

    const rootDropProps = {
        onDragEnter: handleDragEnter,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
    };

    const dialogPaperDropProps = {
        ...rootDropProps,
        sx: dropHighlightSx,
    };

    return { busy, dropHighlightSx, rootDropProps, dialogPaperDropProps };
}
